import type { GameStateData } from '../game/GameState.js';
import { matchWinnerSeats } from '../game/scoring/index.js';
import { teamSeatsOf, teamTotal } from './scoreDisplay.js';

/** Same shape as `t` from `@bn-games/i18n-core`, injectable for unit tests. */
export type ResultTranslator = (key: string, vars?: Record<string, string | number>) => string;

/** Display name for a seat — the caller owns name resolution. */
export type SeatNamer = (seat: number) => string;

export interface ResultText {
  readonly title: string;
  readonly body: string;
  /** Changes whenever the modal content does — lets callers skip redundant DOM rewrites. */
  readonly key: string;
}

type ResultState = Pick<
  GameStateData,
  'ruleSet' | 'scores' | 'teamMap' | 'lastResult' | 'lastAction' | 'carriedPenalty' | 'matchOver'
>;

function joinNames(seats: readonly number[], seatName: SeatNamer): string {
  return seats.map(seatName).join(', ');
}

/** Seat that played out on the hand's last tile, falling back to the first winner. */
function finisherSeat(state: ResultState): number | null {
  const last = state.lastAction;
  if (last !== null && last.kind === 'play' && last.wentOut) return last.seat;
  return state.lastResult?.winnerSeats[0] ?? null;
}

/**
 * Козёл hand summary: how the hand ended («домино» / «рыба» / «яйца» /
 * «лысый») and the penalty each losing side had written. Shared by the
 * result modal and the screen-reader narration.
 */
export function penaltyHandLines(
  state: ResultState,
  seatName: SeatNamer,
  t: ResultTranslator,
): readonly string[] {
  const result = state.lastResult;
  if (result === null) return [];
  if (result.eggs === true) {
    return [t('result.penalty.eggs', { points: state.carriedPenalty })];
  }
  const lines: string[] = [];
  const winners = joinNames(result.winnerSeats, seatName);
  const finisher = finisherSeat(state);
  if (result.baldFinish === true && finisher !== null) {
    lines.push(t('result.penalty.bald', { name: seatName(finisher) }));
  } else {
    lines.push(t(result.blocked ? 'result.penalty.fish' : 'result.penalty.domino', { names: winners }));
  }
  result.perSeatAward.forEach((points, seat) => {
    if (points <= 0) return;
    const names = joinNames(teamSeatsOf(state, seat), seatName);
    lines.push(t('result.penalty.written', { names, points }));
  });
  return lines;
}

/**
 * Seats named as the «козёл»: every side whose total reached the match goal.
 * A «лысый» finish ends the match before anyone reaches it, so there every
 * non-winning seat is the goat.
 */
export function goatSeats(state: ResultState): readonly number[] {
  const winners = matchWinnerSeats(state);
  const losers = state.scores.flatMap((_, seat) => (winners.includes(seat) ? [] : [seat]));
  if (state.lastResult?.baldFinish === true) return losers;
  const reached = losers.filter((seat) => teamTotal(state, seat) >= state.ruleSet.pointGoal);
  return reached.length > 0 ? reached : losers;
}

/** Match-over line naming the goat(s), or null when nobody lost. */
export function goatLine(state: ResultState, seatName: SeatNamer, t: ResultTranslator): string | null {
  const goats = goatSeats(state);
  if (goats.length === 0) return null;
  const names = joinNames(goats, seatName);
  return goats.length > 1
    ? t('result.penalty.goats', { names })
    : t('result.penalty.goat', { name: names });
}

function fivesBody(state: ResultState, seatName: SeatNamer, t: ResultTranslator): string {
  // Match over names the match winners, not the last hand's: the match can
  // end on a Muggins claim, or a scoring play can put someone other than the
  // player who went out on top, so "went out" would name the wrong seat.
  if (state.matchOver) {
    const matchWinners = matchWinnerSeats(state);
    if (matchWinners.length === 0) return t('result.drawn');
    return t('result.matchWon', { names: joinNames(matchWinners, seatName) });
  }
  const winners = state.lastResult?.winnerSeats ?? [];
  if (winners.length === 0) return t('result.drawn');
  const names = joinNames(winners, seatName);
  return state.lastResult?.blocked === true
    ? t('result.blocked', { names })
    : t('result.wentOut', { names });
}

function penaltyBody(state: ResultState, seatName: SeatNamer, t: ResultTranslator): string {
  const lines = [...penaltyHandLines(state, seatName, t)];
  if (state.matchOver) {
    const goat = goatLine(state, seatName, t);
    if (goat !== null) lines.push(goat);
  }
  return lines.join(' ');
}

/** Title + body of the hand-over / match-over result modal. */
export function buildResultText(
  state: ResultState,
  seatName: SeatNamer,
  t: ResultTranslator,
): ResultText {
  const title = t(state.matchOver ? 'result.matchOver' : 'result.handOver');
  const body = state.ruleSet.scoring === 'penalty'
    ? penaltyBody(state, seatName, t)
    : fivesBody(state, seatName, t);
  return { title, body, key: `${title}|${body}` };
}
